/**
 * Query options factories for the patient record feature.
 *
 * Components compose these directly with `useQuery` — there is no per-endpoint
 * hook wrapper. The cache key, fetch function, and stale-time policy live in
 * one place so invalidation is grep-able from the call site:
 *
 *   const notes = useQuery(patientQueries.notes(guid));
 *   queryClient.invalidateQueries(patientQueries.notes(guid));
 *
 * Each factory returns the full `QueryOptions` object — components needing a
 * one-off override (e.g. zero stale-time on a refresh button) can spread it:
 *
 *   useQuery({ ...patientQueries.notes(guid), staleTime: 0 });
 *
 * The shared `personKey` prefix makes a `queryClient.invalidateQueries({
 * queryKey: ['person', guid] })` invalidate every section of a single record
 * in one call.
 */

import { queryOptions } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { ApiError } from '@/api/errors';
import { mapPatient } from '@/api/mappers';
import { mapFamilySummaries } from '@/features/families/mappers';
import { mapCareTeamMemberRow } from '@/features/patient-record/api/careTeam';
import type {
  AllergyProfileViewModel,
  MeasurementViewModel,
  MedicalHistoryViewModel,
} from '@/features/patient-record/types';
import type { FamilySummaryViewModel } from '@/models/family';
import type { CareTeamMemberDetailViewModel } from '@/models/patient';
import {
  mapAllergyProfile,
  mapCarePathways,
  mapCarePathwayTemplate,
  mapCarePathwayTemplates,
  mapConsents,
  mapDiagnoses,
  mapDocuments,
  mapFeatures,
  mapGeneticVariants,
  mapDeliveries,
  mapMeasurements,
  mapMedicalHistory,
  mapNotes,
  mapQuestionnaires,
  mapTask,
  mapTaskSummaries,
  mapTestOrders,
} from './mappers';

const personKey = (guid: string): readonly ['person', string] =>
  ['person', guid] as const;

const DEFAULT_STALE_TIME = 60_000;

const EMPTY_MEDICAL_HISTORY: MedicalHistoryViewModel = {
  indicationForReferral: null,
  medicalAndDevelopmentalHistory: null,
};

const EMPTY_ALLERGY_PROFILE: AllergyProfileViewModel = {
  id: null,
  nkda: false,
  allergies: [],
};

/**
 * Some BE collection endpoints respond 404 when the patient simply has
 * no record yet (medical history without a row, measurements list with
 * no rows, …). Surfacing that as a hard error masks the whole section —
 * we want an empty state instead, with the "Add" affordance still
 * reachable. This swallows only `not_found`; every other failure
 * (network, 500, …) still propagates.
 */
function emptyOnNotFound<T>(fallback: T): (error: unknown) => T {
  return (error) => {
    if (error instanceof ApiError && error.code === 'not_found') {
      return fallback;
    }
    throw error;
  };
}

export const patientQueries = {
  detail: (guid: string) =>
    queryOptions({
      queryKey: personKey(guid),
      queryFn: ({ signal }) =>
        apiClient.getPerson(guid, { signal }).then(mapPatient),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  features: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'features'] as const,
      queryFn: ({ signal }) =>
        apiClient.getFeatures(guid, { signal }).then(mapFeatures),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  diagnoses: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'diagnoses'] as const,
      queryFn: ({ signal }) =>
        apiClient.getDiagnoses(guid, { signal }).then(mapDiagnoses),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  geneticVariants: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'genetic-info'] as const,
      queryFn: ({ signal }) =>
        apiClient.getGeneticVariants(guid, { signal }).then(mapGeneticVariants),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  medicalHistory: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'medical-history'] as const,
      queryFn: ({ signal }) =>
        apiClient
          .getMedicalHistory(guid, { signal })
          .then(mapMedicalHistory)
          .catch(emptyOnNotFound<MedicalHistoryViewModel>(EMPTY_MEDICAL_HISTORY)),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  measurements: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'measurements'] as const,
      queryFn: ({ signal }) =>
        apiClient
          .getMeasurements(guid, { signal })
          .then(mapMeasurements)
          .catch(emptyOnNotFound<MeasurementViewModel[]>([])),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  allergyProfile: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'allergy-profile'] as const,
      queryFn: ({ signal }) =>
        apiClient
          .getAllergyProfile(guid, { signal })
          .then(mapAllergyProfile)
          .catch(emptyOnNotFound<AllergyProfileViewModel>(EMPTY_ALLERGY_PROFILE)),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  notes: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'notes'] as const,
      queryFn: ({ signal }) =>
        apiClient.getNotes(guid, { signal }).then(mapNotes),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  documents: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'documents'] as const,
      queryFn: ({ signal }) =>
        apiClient.getDocuments(guid, { signal }).then(mapDocuments),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  testOrders: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'test-orders'] as const,
      queryFn: ({ signal }) =>
        apiClient.getTestOrders(guid, { signal }).then(mapTestOrders),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  questionnaires: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'questionnaires'] as const,
      queryFn: ({ signal }) =>
        apiClient.getQuestionnaires(guid, { signal }).then(mapQuestionnaires),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  // Submission artifacts (PDF/JSON) for one questionnaire request. Lazy: the row enables it when
  // its inline disclosure is opened.
  questionnaireAttachments: (guid: string, requestGuid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'questionnaires', requestGuid, 'attachments'] as const,
      queryFn: ({ signal }) => apiClient.getQuestionnaireAttachments(guid, requestGuid, { signal }),
      enabled: false,
      staleTime: DEFAULT_STALE_TIME,
    }),

  // Delivery ("push") attempts for one questionnaire request (PTV2-1162). The row enables it for
  // EMAIL questionnaires to show the latest delivery status + attempt history.
  questionnaireDeliveries: (guid: string, requestGuid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'questionnaires', requestGuid, 'deliveries'] as const,
      queryFn: ({ signal }) =>
        apiClient.getQuestionnaireDeliveries(guid, requestGuid, { signal }).then(mapDeliveries),
      enabled: false,
      staleTime: DEFAULT_STALE_TIME,
    }),

  consents: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'consents'] as const,
      queryFn: ({ signal }) =>
        apiClient.getConsents(guid, { signal }).then(mapConsents),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  carePathways: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'care-pathways'] as const,
      queryFn: ({ signal }) =>
        apiClient.getCarePathways(guid, { signal }).then(mapCarePathways),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  /**
   * Live care-team membership rows (PTV2-1095). Separate query from
   * `detail(guid)` so the panel can re-fetch on its own cadence and the four
   * care-team mutations have a focused cache to update optimistically.
   */
  careTeamMembers: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'care-team'] as const,
      queryFn: async ({ signal }): Promise<readonly CareTeamMemberDetailViewModel[]> => {
        const rows = await apiClient.getCareTeam(guid, { signal });
        const mapped: CareTeamMemberDetailViewModel[] = [];
        for (const dto of rows) {
          const row = mapCareTeamMemberRow(dto);
          if (row !== null) mapped.push(row);
        }
        return mapped;
      },
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  /**
   * Per-patient task summaries (PTV2-1097). Pass `status='all'` for the
   * full set, otherwise the BE defaults to `todo`-only — which matches
   * the side panel + header chip view.
   */
  tasks: (guid: string, filter: { status?: 'TODO' | 'DONE' | 'CANCELLED' | 'ALL' } = {}) =>
    queryOptions({
      queryKey: [...personKey(guid), 'tasks', filter.status ?? 'TODO'] as const,
      queryFn: ({ signal }) => {
        const wireStatus = filter.status === undefined ? 'todo' : filter.status.toLowerCase();
        return apiClient
          .listPersonTasks(
            guid,
            { status: wireStatus as 'todo' | 'done' | 'cancelled' | 'all', size: 200 },
            { signal },
          )
          .then((page) => mapTaskSummaries(page, guid));
      },
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  /**
   * Families the person is a member of (PTV2-1160). Consumed by the
   * patient-record Family right-rail panel to decide linked vs unlinked —
   * the person response itself doesn't carry a `family_guid`. Unknown
   * person guid → 404 → surfaced as an empty list so the panel falls back
   * to the unlinked CTAs instead of an error (the parent page already 404s
   * on an unknown patient).
   */
  families: (guid: string) =>
    queryOptions({
      queryKey: [...personKey(guid), 'families'] as const,
      queryFn: ({ signal }): Promise<readonly FamilySummaryViewModel[]> =>
        apiClient
          .listPersonFamilies({ guid }, { signal })
          .then((page) => mapFamilySummaries(page.content))
          .catch(emptyOnNotFound<readonly FamilySummaryViewModel[]>([])),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  /** Single-task detail used by the edit modal. */
  task: (patientGuid: string, taskId: string) =>
    queryOptions({
      queryKey: [...personKey(patientGuid), 'tasks', 'detail', taskId] as const,
      queryFn: ({ signal }) =>
        apiClient
          .getPersonTask(patientGuid, taskId, { signal })
          .then((dto) => mapTask(dto, patientGuid)),
      enabled: patientGuid.length > 0 && taskId.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),
};

/** Common prefix — invalidates everything for a single patient in one call. */
export const patientRecordKeys = {
  all: personKey,
};

/**
 * Tenant-scoped catalogs consumed by patient-record editors (program select,
 * GC user picker). Cached longer than per-patient queries — they change at
 * tenant-admin cadence, not per-record.
 */
const TENANT_STALE_TIME = 5 * 60_000;

export const tenantQueries = {
  programs: () =>
    queryOptions({
      queryKey: ['tenant', 'programs'] as const,
      queryFn: ({ signal }) => apiClient.listTenantPrograms({ signal }),
      staleTime: TENANT_STALE_TIME,
    }),

  users: (params: { role?: string; q?: string } = {}) =>
    queryOptions({
      queryKey: ['tenant', 'users', params] as const,
      queryFn: ({ signal }) => apiClient.listTenantUsers(params, { signal }),
      staleTime: TENANT_STALE_TIME,
    }),

  carePathwayTemplates: (params: { include_disabled?: boolean } = {}) =>
    queryOptions({
      queryKey: ['tenant', 'care-pathway-templates', params] as const,
      queryFn: ({ signal }) =>
        apiClient
          .listCarePathwayTemplates(params, { signal })
          .then(mapCarePathwayTemplates),
      staleTime: TENANT_STALE_TIME,
    }),

  carePathwayTemplate: (id: string) =>
    queryOptions({
      queryKey: ['tenant', 'care-pathway-template', id] as const,
      queryFn: ({ signal }) =>
        apiClient.getCarePathwayTemplate(id, { signal }).then(mapCarePathwayTemplate),
      enabled: id.length > 0,
      staleTime: TENANT_STALE_TIME,
    }),
};
